import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  UserGroupIcon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
  UserPlusIcon
} from '@heroicons/react/24/outline';
import { Team, User } from '../../types';
import { teamsAPI, usersAPI } from '../../services/api';
import Layout from '../../components/layout/Layout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import TeamModal from '../../components/admin/TeamModal';
import TeamDetailModal from '../../components/admin/TeamDetailModal';
import AddMembersModal from '../../components/admin/AddMembersModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import toast from 'react-hot-toast';

const Teams: React.FC = () => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState<Team | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addMembersTeam, setAddMembersTeam] = useState<Team | null>(null);
  const teamsPerPage = 12;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [teamsData, usersData] = await Promise.all([
        teamsAPI.getAll(),
        usersAPI.getAll(),
      ]);
      setTeams(teamsData);
      setUsers(usersData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load teams data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateTeam = async (teamData: any) => {
    try {
      setIsSubmitting(true);
      const newTeam = await teamsAPI.create(teamData);
      setTeams([...teams, newTeam]);
      setIsModalOpen(false);
      toast.success('Team created successfully');
    } catch (error) {
      toast.error('Failed to create team');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateTeam = async (teamData: any) => {
    if (!editingTeam) return;
    
    try {
      setIsSubmitting(true);
      const updatedTeam = await teamsAPI.update(editingTeam.id, teamData);
      setTeams(teams.map(team => team.id === editingTeam.id ? updatedTeam : team));
      setIsModalOpen(false);
      setEditingTeam(null);
      toast.success('Team updated successfully');
    } catch (error) {
      toast.error('Failed to update team');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteTeam = (team: Team) => {
    setTeamToDelete(team);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteTeam = async () => {
    if (!teamToDelete) return;
    
    try {
      setIsSubmitting(true);
      await teamsAPI.delete(teamToDelete.id);
      setTeams(teams.filter(team => team.id !== teamToDelete.id));
      setIsDeleteDialogOpen(false);
      setTeamToDelete(null);
      toast.success('Team deleted successfully');
    } catch (error) {
      toast.error('Failed to delete team');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewTeam = (team: Team) => {
    setSelectedTeam(team);
    setIsDetailModalOpen(true);
  };

  const openEditModal = (team: Team) => {
    setEditingTeam(team);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingTeam(null);
  };

  const handleAddMembers = async (teamId: string, userIds: string[]) => {
    try {
      setIsSubmitting(true);
      const updatedTeam = await teamsAPI.addMembers(teamId, { userIds });
      setTeams(teams.map(team => team.id === teamId ? updatedTeam : team));
      setAddMembersTeam(null);
      toast.success('Members added successfully');
    } catch (error) {
      console.error('Error adding members:', error);
      toast.error('Failed to add members');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filter teams based on search term
  const filteredTeams = teams.filter(team =>
    team.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    team.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    team.manager.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    team.manager.lastName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination logic
  const totalPages = Math.ceil(filteredTeams.length / teamsPerPage);
  const startIndex = (currentPage - 1) * teamsPerPage;
  const paginatedTeams = filteredTeams.slice(startIndex, startIndex + teamsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center space-y-4 lg:space-y-0">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Teams Management</h1>
            <p className="text-gray-600">
              Manage teams, performance, and member assignments ({filteredTeams.length} teams)
            </p>
          </div>
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search teams..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm w-full sm:w-64"
              />
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="btn-primary flex items-center justify-center space-x-2"
            >
              <PlusIcon className="h-5 w-5" />
              <span>Create Team</span>
            </button>
          </div>
        </div>

        {/* Teams Grid */}
        {paginatedTeams.length === 0 ? (
          <div className="text-center py-12">
            <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No teams found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? 'No teams match your search criteria.' : 'Get started by creating a new team.'}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {paginatedTeams.map((team, index) => (
                <motion.div
                  key={team.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className="card hover:shadow-lg transition-all duration-200 cursor-pointer"
                  onClick={() => handleViewTeam(team)}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: team.color }}
                      />
                      <h3 className="font-semibold text-gray-900 truncate">{team.name}</h3>
                    </div>
                    <div className="flex space-x-1" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => handleViewTeam(team)}
                        className="p-1 text-blue-600 hover:text-blue-800 transition-colors"
                        title="View Details & Manage Members"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setAddMembersTeam(team)}
                        className="p-1 text-green-600 hover:text-green-800 transition-colors"
                        title="Add Members"
                      >
                        <UserPlusIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => openEditModal(team)}
                        className="p-1 text-primary-600 hover:text-primary-800 transition-colors"
                        title="Edit Team"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteTeam(team)}
                        className="p-1 text-red-600 hover:text-red-800 transition-colors"
                        title="Delete Team"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {team.description && (
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                      {team.description}
                    </p>
                  )}

                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Manager</span>
                      <span className="font-medium text-gray-900">
                        {team.manager.firstName} {team.manager.lastName}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Members</span>
                      <div className="text-right">
                        <span className="font-medium text-gray-900">
                          {team.members.length}
                        </span>
                        <div className="text-xs text-gray-400">
                          Click view to manage
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Projects</span>
                      <span className="font-medium text-gray-900">
                        {team._count?.projects || 0}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Tasks</span>
                      <span className="font-medium text-gray-900">
                        {team._count?.tasks || 0}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                      <div className="flex items-center space-x-1">
                        <ChartBarIcon className="h-3 w-3" />
                        <span>Performance</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <ClipboardDocumentListIcon className="h-3 w-3" />
                        <span>Analytics</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center space-x-2 mt-8">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let page: number;
                  if (totalPages <= 5) {
                    page = i + 1;
                  } else if (currentPage <= 3) {
                    page = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    page = totalPages - 4 + i;
                  } else {
                    page = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                        currentPage === page
                          ? 'bg-primary-600 text-white'
                          : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}

        {/* Team Modal */}
        <TeamModal
          isOpen={isModalOpen}
          onClose={closeModal}
          onSubmit={editingTeam ? handleUpdateTeam : handleCreateTeam}
          team={editingTeam}
          users={users}
          isLoading={isSubmitting}
        />

        {/* Team Detail Modal */}
        <TeamDetailModal
          isOpen={isDetailModalOpen}
          onClose={() => {
            setIsDetailModalOpen(false);
            setSelectedTeam(null);
          }}
          team={selectedTeam}
          onTeamUpdate={(updatedTeam) => {
            setTeams(teams.map(team => team.id === updatedTeam.id ? updatedTeam : team));
          }}
        />

        {/* Add Members Modal */}
        {addMembersTeam && (
          <AddMembersModal
            isOpen={!!addMembersTeam}
            onClose={() => setAddMembersTeam(null)}
            onSubmit={(userIds) => handleAddMembers(addMembersTeam.id, userIds)}
            team={addMembersTeam}
            users={users}
          />
        )}

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => {
            setIsDeleteDialogOpen(false);
            setTeamToDelete(null);
          }}
          onConfirm={confirmDeleteTeam}
          title="Delete Team"
          message={`Are you sure you want to delete "${teamToDelete?.name}"? This action cannot be undone and will affect all associated tasks.`}
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
          isLoading={isSubmitting}
        />
      </div>
    </Layout>
  );
};

export default Teams;
